import React, { useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Grid,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormGroup,
  Box,
  Typography,
  Card,
  IconButton,
  CardHeader,
  CardContent,
  Divider,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AddIcon from "@mui/icons-material/Add";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { userValidationCreateSchema } from "./utils/userValidationSchema";
import { useEffect } from "react";
import { BaseFormWrapper } from "../../components/shared/BaseFormWrapper";
import { getUsers, postUser } from "../API/userAPI";

const CreateUserComponent = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirm_password: "",
    firstname: "",
    lastname: "",
    middle_initial: "",
    email: "",
    date_hired: "",
    roles: [],
    contact: [{ type: "", country_code: "", number: "", description: "" }],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  useEffect(() => {
    (async () => {
      const list = await getUsers();
      if (Array.isArray(list)) setUsers(list);
      else setUsers([]); // fall back so validation still runs safely
    })();
  }, []);

  const capitalizeWords = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleInputChangeWithCapitalization = (e) => {
    const { name, value } = e.target;
    const capitalizedValue = capitalizeWords(value);
    handleInputChange({ target: { name, value: capitalizedValue } });
  };

  const handleClickShowPassword = (field) => {
    setShowPasswords((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleRoleChange = (e) => {
    const { value, checked } = e.target;

    setFormData((prevFormData) => {
      let updatedRoles;

      if (checked) {
        updatedRoles = [...prevFormData.roles, value];
      } else {
        updatedRoles = prevFormData.roles.filter((role) => role !== value);
      }

      return {
        ...prevFormData,
        roles: updatedRoles,
      };
    });
  };

  const handleContactChange = (index, field, value) => {
    const updatedContact = formData.contact.map((contact, i) =>
      i === index
        ? {
          ...contact,
          [field]: field === "type" ? value.toLowerCase() : value,
        }
        : contact
    );
    setFormData({
      ...formData,
      contact: updatedContact,
    });
  };

  const addContactField = () => {
    setFormData({
      ...formData,
      contact: [
        ...formData.contact,
        { type: "", country_code: "", number: "", description: "" },
      ],
    });
  };

  const removeContactField = (index) => {
    if (formData.contact.length > 1) {
      const updatedContact = formData.contact.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        contact: updatedContact,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await userValidationCreateSchema(null, users).validate(formData, {
        abortEarly: false,
      });

      setErrors({});
      setErrorMessage("");
      setIsLoading(true);

      const { confirm_password, ...submitData } = formData;

      try {
        // const response = await fetch(
        //   "https://ugdmn5pwqb.execute-api.ap-southeast-1.amazonaws.com/prod/postUser",
        //   {
        //     method: "POST",
        //     headers: {
        //       "Content-Type": "application/json",
        //     },
        //     body: JSON.stringify(submitData),
        //   }
        // );
        const responseData = await postUser(submitData);

        if (responseData) {
          setSuccessMessage("User created successfully!");
          setTimeout(() => {
            navigate("/manage-users");
          }, 3000);
        } else {
          const result = await responseData.json();

          setErrorMessage(
            result.message ||
            "An unexpected error occurred while creating the User. Please try again later."
          );
        }
      } catch (error) {
        setErrorMessage(
          `An error occurred while creating the User: ${error.message}. Please check your network connection and try again.`
        );
      }
    } catch (validationErrors) {
      if (validationErrors.inner) {
        const formattedErrors = validationErrors.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});

        setErrors(formattedErrors);
      } else {
        setErrorMessage(
          validationErrors.message ||
          "Validation failed. Please check your input and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseFormWrapper
      titleIcon={<AdminPanelSettingsIcon />}
      title="Admin Hub"
      subTitle="Users"
      subTitleRoute="/manage-users"
      formTitle="Create User"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
        noValidate
        autoComplete="off"
      >
        <Card
          variant="outlined"
        >
          <CardHeader
            title={
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "1rem",
                }}
              >
                Basic Information
              </Typography>
            }
            sx={{ padding: "16px" }}
          />
          <CardContent sx={{ padding: "20px" }}>
            <Grid container spacing={2}>
              {/* First Name */}
              <Grid item xs={12} md={12} lg={4}>
                <TextField
                  label="First Name"
                  id="firstname"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleInputChangeWithCapitalization}
                  size="small"
                  required
                  fullWidth
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                  disabled={isLoading}
                />
              </Grid>

              {/* Middle Initial */}
              <Grid item xs={12} md={12} lg={4}>
                <TextField
                  label="Middle Initial"
                  id="middle_initial"
                  name="middle_initial"
                  value={formData.middle_initial}
                  onChange={handleInputChangeWithCapitalization}
                  size="small"
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} md={12} lg={4}>
                <TextField
                  label="Last Name"
                  id="lastname"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleInputChangeWithCapitalization}
                  size="small"
                  required
                  fullWidth
                  error={!!errors.lastname}
                  helperText={errors.lastname}
                  disabled={isLoading}
                />
              </Grid>

              {/* Username */}
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label="Username"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  size="small"
                  required
                  fullWidth
                  error={!!errors.username}
                  helperText={errors.username}
                  disabled={isLoading}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label="Email"
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  size="small"
                  required
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={isLoading}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label="Password"
                  type={showPasswords.password ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  size="small"
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleClickShowPassword("password")}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPasswords.password ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={isLoading}
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label="Confirm Password"
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  size="small"
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            handleClickShowPassword("confirmPassword")
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPasswords.confirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password}
                  disabled={isLoading}
                />
              </Grid>

              {/* Date Hired */}
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label="Date Hired"
                  id="date_hired"
                  name="date_hired"
                  type="date"
                  value={formData.date_hired}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider
                variant="fullWidth"
                sx={{
                  my: 4,
                  borderColor: "rgba(0, 0, 0, 0.12)",
                  borderWidth: "0.5px",
                }}
              />
            </Grid>

            {/* Contact Info */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CardHeader
                  title={
                    <Box
                      display="flex"
                      alignItems="center"
                      width="100%"
                      flexWrap="wrap"
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm="auto">
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              fontSize: "1rem",
                            }}
                            mr={2}
                          >
                            Contact Information (optional)
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                          {/* Add another contact button */}
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={addContactField}
                            size="small"
                            disabled={isLoading}
                          >
                            Add Another Contact Person
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                  sx={{ padding: "16px" }}
                />
              </Grid>
              <Grid item xs={12}>
                <CardContent sx={{ padding: "20px" }}>
                  <Grid container spacing={2}>
                    {formData.contact.map((contact, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12} md={12} lg={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`contact-type-${index}`}>
                              Type
                            </InputLabel>
                            <Select
                              labelId={`contact-type-${index}`}
                              value={contact.type}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "type",
                                  e.target.value
                                )
                              }
                              label="Type"
                              size="small"
                              disabled={isLoading}
                            >
                              <MenuItem value="mobile">Mobile</MenuItem>
                              <MenuItem value="landline">Landline</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={12} lg={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`country-code-${index}`}>
                              Country Code
                            </InputLabel>
                            <Select
                              labelId={`country-code-${index}`}
                              value={contact.country_code}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "country_code",
                                  e.target.value
                                )
                              }
                              label="Country Code"
                              size="small"
                              disabled={isLoading}
                            >
                              <MenuItem value="+63">+63</MenuItem>
                              <MenuItem value="+1">+1</MenuItem>
                              <MenuItem value="+44">+44</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={12} lg={3}>
                          <TextField
                            label="Contact Number"
                            id={`contact_number_${index}`}
                            type="text"
                            value={contact.number}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "number",
                                e.target.value
                              )
                            }
                            size="small"
                            fullWidth
                            error={!!errors.contact?.[index]?.number}
                            helperText={errors.contact?.[index]?.number}
                            disabled={isLoading}
                          />
                        </Grid>

                        <Grid item xs={12} md={12} lg={4}>
                          <TextField
                            label="Description"
                            id={`contact_description_${index}`}
                            value={contact.description}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            size="small"
                            fullWidth
                            disabled={isLoading}
                          />
                        </Grid>

                        <Grid item xs={12} md={12} lg={1}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent={{
                              xs: "center",
                              md: "center",
                              lg: "flex-start",
                            }}
                            height="100%"
                          >
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => removeContactField(index)}
                                  disabled={
                                    isLoading || formData.contact.length === 1
                                  }
                                >
                                  <DeleteIcon
                                    sx={{
                                      fontSize: "1.5rem",
                                    }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </CardContent>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider
                variant="fullWidth"
                sx={{
                  my: 4,
                  borderColor: "rgba(0, 0, 0, 0.12)",
                  borderWidth: "0.5px",
                }}
              />
            </Grid>

            {/* Roles */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      User Roles *
                    </Typography>
                  }
                  sx={{ padding: "16px" }}
                />
                <CardContent sx={{ padding: "20px" }}>
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={6} lg={6}>
                      <FormGroup>
                        {[
                          "sales",
                          "operations_admin",
                          "operations_coordinator",
                          "operations_technician",
                        ].map((role, index) => (
                          <FormControlLabel
                            key={index}
                            control={
                              <Checkbox
                                id={role}
                                name="roles"
                                value={role}
                                checked={formData.roles.includes(role)}
                                onChange={handleRoleChange}
                                disabled={isLoading}
                              />
                            }
                            label={role
                              .replace(/_/g, " ")
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <FormGroup>
                        {[
                          "finance",
                          "account_manager",
                          "office_admin",
                          "customer_service",
                          "system_admin",
                        ].map((role, index) => (
                          <FormControlLabel
                            key={index}
                            control={
                              <Checkbox
                                id={role}
                                name="roles"
                                value={role}
                                checked={formData.roles.includes(role)}
                                onChange={handleRoleChange}
                                disabled={isLoading}
                              />
                            }
                            label={role
                              .replace(/_/g, " ")
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                    {errors.roles && (
                      <Grid item xs={12}>
                        <FormHelperText error>{errors.roles}</FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              {/* Form buttons */}
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent={{
                  xs: "center",
                  md: "center",
                  lg: "flex-end",
                }}
                alignItems="center"
                mt={2}
                mb={2}
                mr={2}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/manage-users")}
                  disabled={isLoading}
                  sx={{
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    color: "#FF4C4C",
                    borderColor: "#FF4C4C",
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    marginLeft: "10px",
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                  }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>

          </CardContent>
        </Card>
      </form>
    </BaseFormWrapper>
  );
};

export default CreateUserComponent;
